import type { WidgetTabSlice, WidgetTabSliceEventBus } from '.';
import { signal } from '@preact/signals-react';
import type { Signal } from '@preact/signals-react';
import { optional } from 'inversify';
import { inject, injectable } from 'src/features/ioc';
import type { WorkspaceService } from 'src/services/WorkspaceService';
import type { RootState } from 'src/store';
import { removeContainer, setMaximizedContainer } from 'src/store/actions/container';
import { removeTab, updateActiveTab, updateTabName } from 'src/store/actions/tab';
import type { ByIdState } from 'src/store/reducers/tab/byId';
import { getMaximizedContainerId } from 'src/store/selectors';
import type { ContainerViewModel, WidgetTabViewModel } from 'src/store/types';
import type { ReactBindings } from 'src/types/bindings';
import { EventBusBuilder } from 'src/types/EventBus/EventBus';

/**
 * Implementation of WidgetTabSlice - a standalone injectable for widget tab and container state management.
 *
 * This class is designed to be composed with other slices to build widget functionality.
 * It manages tab/container state backed by Redux store.
 */
@injectable()
export class WidgetTabSliceImpl implements WidgetTabSlice {
    #disposableStack = new DisposableStack();
    #tabId: Signal<string> = signal('');
    #widgetType: Signal<string> = signal('');
    #name: Signal<string> = signal('');
    #isMaximized: Signal<boolean> = signal(false);
    #container: Signal<ContainerViewModel> = signal({} as ContainerViewModel);
    #containerActiveTab: Signal<WidgetTabViewModel | undefined> = signal(undefined);
    #tabsById: Signal<ByIdState> = signal({});
    #containerId: string | null = null;
    #storeUnsubscribe: (() => void) | null = null;
    #store: ReactBindings['Store'];

    constructor(
        @inject('Store') store: ReactBindings['Store'],
        @inject('WorkspaceService') @optional() private readonly workspaceService?: WorkspaceService,
        @inject('Logger') @optional() private readonly logger?: ReactBindings['Logger'],
    ) {
        this.#store = store;
    }

    /**
     * The event bus exposes signals for reading state and events for modifying state.
     *
     * Signals:
     * - `widget::tabId`: The current tab ID
     * - `widget::type`: The widget type (e.g. 'watchlist', 'screener')
     * - `widget::name`: The display name of the widget/tab
     * - `widget::isMaximized`: Whether the container is maximized
     * - `widget::container`: The container view model
     * - `widget::containerActiveTab`: The active tab in the container
     * - `widget::tabsById`: All tabs by ID
     *
     * Events:
     * - `widget::toggleMaximized`: Toggle the maximized state
     * - `widget::delete`: Delete the widget/container
     * - `widget::changeTab`: Change to a different tab
     * - `widget::deleteTab`: Delete a specific tab
     * - `widget::changeTabName`: Change a tab's name
     */
    eventBus: WidgetTabSliceEventBus = EventBusBuilder.create()
        // Signals
        .addSignal('widget::tabId', () => this.#tabId)
        .addSignal('widget::type', () => this.#widgetType)
        .addSignal('widget::name', () => this.#name)
        .addSignal('widget::isMaximized', () => this.#isMaximized)
        .addSignal('widget::container', () => this.#container)
        .addSignal('widget::containerActiveTab', () => this.#containerActiveTab)
        .addSignal('widget::tabsById', () => this.#tabsById)
        // Events
        .addEvent('widget::toggleMaximized', () => {
            if (this.#containerId) {
                const isMaximized = getMaximizedContainerId(this.#store.getState()) === this.#containerId;
                this.#store.dispatch(setMaximizedContainer(isMaximized ? undefined : this.#containerId));
            }
        })
        .addEvent('widget::delete', () => {
            if (this.#containerId) {
                if (APP_ELECTRON && this.workspaceService) {
                    this.#removeContainerImperatively(this.#containerId);
                    return;
                }
                this.#store.dispatch(removeContainer(this.#containerId));
            }
        })
        .addEvent('widget::changeTab', (payload) => {
            if (this.#containerId) {
                this.#store.dispatch(updateActiveTab(this.#containerId, payload.tabId));
            }
        })
        .addEvent('widget::deleteTab', (payload) => {
            if (this.#containerId) {
                const state = this.#store.getState();
                const parentContainer = state.container.byId[this.#containerId];
                const normalizedTabsByKey = state.tab.byId;

                if (parentContainer.tabIds.length === 1) {
                    if (APP_ELECTRON && this.workspaceService) {
                        this.#removeContainerImperatively(parentContainer.id);
                        return;
                    }
                    this.#store.dispatch(removeContainer(parentContainer.id));
                } else {
                    if (APP_ELECTRON && this.workspaceService) {
                        this.#removeWidgetImperatively(payload.tabKey);
                        return;
                    }
                    const newActiveTabKey = parentContainer.tabIds.find((key) => key !== payload.tabKey);
                    const newActiveTabId = normalizedTabsByKey[newActiveTabKey!].id;
                    const widgetType = normalizedTabsByKey[payload.tabKey]?.widget?.type ?? 'unknown';
                    this.#store.dispatch(removeTab(payload.tabKey, parentContainer.id, newActiveTabId!, widgetType));
                }
            }
        })
        .addEvent('widget::changeTabName', (payload) => {
            this.#store.dispatch(updateTabName(payload.tab.id, payload.name));
        })
        .build();

    #removeContainerImperatively(containerId: string): void {
        if (!this.workspaceService) return;
        void this.workspaceService.removeContainer(containerId).catch((error: unknown) => {
            this.logger?.error({ message: 'WorkspaceService.removeContainer failed', details: error });
        });
    }

    #removeWidgetImperatively(tabId: string): void {
        if (!this.workspaceService) return;
        void this.workspaceService.removeWidget(tabId).catch((error: unknown) => {
            this.logger?.error({ message: 'WorkspaceService.removeWidget failed', details: error });
        });
    }

    /**
     * Initialize the slice with a tab ID.
     *
     * This subscribes to store changes for the tab/container state and dispatches
     * appropriate actions when events are emitted.
     *
     * @param tabId The tab ID to bind this slice to
     */
    init(tabId: string): void {
        // Clean up existing subscription
        if (this.#storeUnsubscribe) {
            this.#storeUnsubscribe();
            this.#storeUnsubscribe = null;
        }

        const state = this.#store.getState();
        const tab = state.tab.byId[tabId];
        const containerId = tab?.containerId ?? null;
        this.#containerId = containerId;

        // Selectors
        const selectTab = (state: RootState) => state.tab.byId[tabId];
        const selectName = (state: RootState) => selectTab(state)?.name ?? '';
        const selectIsMaximized = (state: RootState) =>
            containerId ? getMaximizedContainerId(state) === containerId : false;
        const selectContainer = (state: RootState) =>
            containerId ? state.container.byId[containerId] : ({} as ContainerViewModel);
        const selectTabsById = (state: RootState) => state.tab.byId;

        // Initialize signals with real store values
        this.#tabId = signal(tabId);
        this.#widgetType = signal(tab?.widget?.type ?? '');
        this.#name = signal(selectName(state));
        this.#isMaximized = signal(selectIsMaximized(state));
        this.#container = signal(selectContainer(state));
        this.#containerActiveTab = signal(tab);
        this.#tabsById = signal(selectTabsById(state));

        // Subscribe to store changes
        this.#storeUnsubscribe = this.#store
            .subscribeSelector((state) => {
                // Check if the tab still exists
                const tabExists = state.tab.byId[tabId] !== undefined;
                if (!tabExists) {
                    return null;
                }
                return {
                    tab: selectTab(state),
                    name: selectName(state),
                    isMaximized: selectIsMaximized(state),
                    container: selectContainer(state),
                    tabsById: selectTabsById(state),
                };
            })
            .handle((data) => {
                if (data !== null) {
                    if (this.#name.value !== data.name) {
                        this.#name.value = data.name;
                    }
                    if (this.#isMaximized.value !== data.isMaximized) {
                        this.#isMaximized.value = data.isMaximized;
                    }
                    if (this.#container.value !== data.container) {
                        this.#container.value = data.container;
                    }
                    if (this.#containerActiveTab.value !== data.tab) {
                        this.#containerActiveTab.value = data.tab;
                    }
                    if (this.#tabsById.value !== data.tabsById) {
                        this.#tabsById.value = data.tabsById;
                    }
                }
            });

        // Register cleanup
        this.#disposableStack.defer(() => {
            if (this.#storeUnsubscribe) {
                this.#storeUnsubscribe();
                this.#storeUnsubscribe = null;
            }
        });
    }

    [Symbol.dispose]() {
        this.#disposableStack.dispose();
    }
}
